import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent } from "@mui/material"
import type { FC } from "react"
import { useMemo, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { Button } from "@/components/common/Button"
import { CancelDialog, SelectDialogContent } from "@/components/common/Modals"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import type { Company } from "@/graphql/codegen/graphql"
import { CompanyStatus } from "@/graphql/codegen/graphql"
import { useGetBaseCompanies } from "@/hooks/useGetBaseCompanies"
import { useUpdateSiblingCompanies } from "@/screens/Companies/edit/components/preferences/hooks/useUpdateSiblingCompanies"
import type { SiblingCompaniesFormValues } from "@/screens/Companies/edit/components/preferences/schemas/siblingCompanies"
import { siblingCompaniesFormSchema } from "@/screens/Companies/edit/components/preferences/schemas/siblingCompanies"

interface EditSiblingCompaniesDialogProps {
  onClose: (shouldRefetch?: boolean) => void
  companyId: string
  initialValues?: {
    companies: Company[]
  }
}

export const EditSiblingCompaniesDialog: FC<EditSiblingCompaniesDialogProps> = ({
  onClose,
  companyId,
  initialValues,
}) => {
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState<boolean>(false)

  const { companies, isFetchingCompanies, loadMoreCompanies } = useGetBaseCompanies({
    statuses: [CompanyStatus.Active],
  })

  const { updateSiblingCompanies, isUpdating } = useUpdateSiblingCompanies()

  const siblingCompanyOptions = useMemo(
    () => companies.filter((company) => company.companyId !== companyId),
    [companies, companyId]
  )

  const isEditMode = !!initialValues
  const dialogTitle = isEditMode ? "Edit Sibling Companies" : "Add Sibling Companies"

  const defaultValues: SiblingCompaniesFormValues = {
    companies: initialValues?.companies ?? [],
  }

  const methods = useForm<SiblingCompaniesFormValues>({
    defaultValues,
    resolver: zodResolver(siblingCompaniesFormSchema),
    mode: "onChange",
  })

  const {
    handleSubmit,
    formState: { isDirty, isSubmitting, isValid },
  } = methods

  const canSubmit = isValid && isDirty && !isSubmitting

  const handleClose = () => {
    if (isDirty) {
      setIsCancelConfirmationOpen(true)
    } else {
      onClose()
    }
  }
  const handleCancelDialogClose = () => {
    setIsCancelConfirmationOpen(false)
  }

  const handleConfirmClose = () => {
    setIsCancelConfirmationOpen(false)
    onClose()
  }

  const onSubmit = async (data: SiblingCompaniesFormValues) => {
    const siblingCompanyIds = data.companies.map((company) => company.companyId)

    const success = await updateSiblingCompanies({
      companyId,
      siblingCompanyIds,
    })
    if (success) {
      onClose(true)
    }
  }

  return (
    <>
      <Dialog open onClose={handleClose} fullWidth maxWidth='sm' data-testid='company.sibling-companies-dialog'>
        <DialogHeader title={dialogTitle} onClose={handleClose} />

        <FormProvider {...methods}>
          <DialogContent className='px-10 pb-1 pt-8'>
            <SelectDialogContent<Company>
              options={siblingCompanyOptions}
              title='Sibling Companies'
              label='Companies sharing pricing agreements'
              idField='companyId'
              nameField='name'
              controlName='companies'
              placeholder='Select At Least One'
              fetching={isFetchingCompanies}
              onLoadMore={loadMoreCompanies}
            />
          </DialogContent>

          <DialogActions className='justify-between p-8'>
            <Button
              appearance='outlined'
              onClick={handleClose}
              disabled={isSubmitting || isUpdating}
              data-testid='company.sibling-companies-dialog.cancel-button'
            >
              Cancel
            </Button>
            <Button
              type='submit'
              disabled={!canSubmit}
              onClick={handleSubmit(onSubmit)}
              data-testid='company.sibling-companies-dialog.save-button'
            >
              Save Changes
            </Button>
          </DialogActions>
        </FormProvider>
      </Dialog>

      {isCancelConfirmationOpen && <CancelDialog onClose={handleCancelDialogClose} onConfirm={handleConfirmClose} />}
    </>
  )
}
